import { FC, Fragment, useEffect, useRef } from "react";
import { Input, PaginationProps, Skeleton } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import ErrorBoundary from "modules/shared/components/ErrorBoundary";
import PageLoader from "modules/shared/components/PageLoader";
import Paginator from "modules/shared/components/Paginator";
import { usePageTitle } from "modules/shared/hooks";
import { useOrganizationRoutes } from "routes/organizationRoute";
import { useProjectDetailsStore } from "modules/organization/store";
import { mnDate } from "modules/shared/services";
import { dateFormat } from "config/commonConfig";
import { useRankingSnapshotStore } from "./store/rankingSnapshotStore";
import RankingSnapshotTableView from "./components/RankingSnapshotTableView";
import "./ranking-snapshot.css";

const { Search } = Input;
const DEFAULT_SKELETON_SUMMARY_CARDS = 4;

const OrgRankingSnapshotPage: FC = () => {
  const { ranking_project_id } = useParams<{ ranking_project_id: string }>();
  const { projectDetails, isLoadingProjectDetails } = useProjectDetailsStore();
  const { organizationBaseRouteUrl } = useOrganizationRoutes();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    header,
    rows,
    isLoading,
    count,
    pageIndex,
    pageSize,
    selectedRanks,
    selectedScores,
    searchTerm,
    loadRankingSnapshot,
    setSearch,
    toggleRankFilter,
    toggleScoreFilter,
    setPage,
    setPageSize,
  } = useRankingSnapshotStore();

  const projectSlug = projectDetails?.slug ?? "";
  const rankingProjectId = Number(ranking_project_id);

  usePageTitle(
    `MonQcle - ${
      projectDetails
        ? projectDetails?.title + " - Ranking Snapshot"
        : "Ranking Snapshot"
    }`,
  );

  const skipNextUrlSyncRef = useRef(true);
  const skipNextScoreUrlSyncRef = useRef(true);

  useEffect(() => {
    skipNextUrlSyncRef.current = true;
    skipNextScoreUrlSyncRef.current = true;
    if (!projectDetails?.slug || !ranking_project_id) {
      return;
    }
    const initialRankFilter = searchParams.getAll("rank_filter");
    const initialScoreFilter = searchParams
      .getAll("score_filter")
      .map(Number)
      .filter((value) => !Number.isNaN(value));
    loadRankingSnapshot(
      projectDetails.slug,
      Number(ranking_project_id),
      initialRankFilter,
      initialScoreFilter,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRankingSnapshot, projectDetails?.slug, ranking_project_id]);

  useEffect(() => {
    if (skipNextUrlSyncRef.current) {
      skipNextUrlSyncRef.current = false;
      return;
    }
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("rank_filter");
        selectedRanks.forEach((rankName) =>
          next.append("rank_filter", rankName),
        );
        return next;
      },
      { replace: true },
    );
  }, [selectedRanks, setSearchParams]);

  useEffect(() => {
    if (skipNextScoreUrlSyncRef.current) {
      skipNextScoreUrlSyncRef.current = false;
      return;
    }
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("score_filter");
        selectedScores.forEach((score) =>
          next.append("score_filter", String(score)),
        );
        return next;
      },
      { replace: true },
    );
  }, [selectedScores, setSearchParams]);

  const getShowingDataString = () => {
    const startIndex = (pageIndex - 1) * pageSize + 1;
    const endIndex =
      count < pageIndex * pageSize ? count : pageIndex * pageSize;
    return `Showing ${startIndex} to ${endIndex} of ${count} Items`;
  };

  const paginationRender: PaginationProps["itemRender"] = (
    _,
    type,
    originalElement,
  ) => {
    if (type === "prev") {
      return <span>Previous</span>;
    }
    if (type === "next") {
      return <span>Next</span>;
    }
    return originalElement;
  };

  const summaryCardCount = header?.medal_summary
    ? header.medal_summary.length
    : DEFAULT_SKELETON_SUMMARY_CARDS;

  return (
    <ErrorBoundary>
      <Fragment>
        {isLoadingProjectDetails ? (
          <PageLoader />
        ) : (
          <Fragment>
            {projectDetails && (
              <Fragment>
                <div className="pageTopSection">
                  <div className="backLink">
                    <Link
                      to={
                        organizationBaseRouteUrl +
                        `/project/${projectDetails?.slug}/ranking-projects`
                      }
                    >
                      <LeftOutlined role="img" aria-label="" />
                      Back to Ranking Projects
                    </Link>
                  </div>
                </div>
                <div className="pageHeadingWrap snapshotHeadingWrap noMargin">
                  <h2 id="tab-header">Snapshot</h2>
                  {isLoading && !header ? (
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: 100 }}
                    />
                  ) : (
                    header?.last_execution && (
                      <span className="snapshotExecutedAt">
                        {mnDate(header.last_execution.executed_at).format(
                          dateFormat.tableDate,
                        )}
                      </span>
                    )
                  )}
                </div>
                <p className="snapshotProjectName">
                  {isLoading && !header ? (
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: 260 }}
                    />
                  ) : (
                    (header?.ranking_project_title ?? "")
                  )}
                </p>
                <div className="snapshotLayoutWrap">
                  <div className="snapshotMainCol">
                    {((isLoading && !header) ||
                      (header?.medal_summary?.length ?? 0) > 0) && (
                      <div className="snapshotSummaryWrap">
                        {isLoading && !header
                          ? Array.from(
                              { length: summaryCardCount },
                              (_, index) => (
                                <div
                                  className="snapshotSummaryCard"
                                  key={`skeleton-summary-${index}`}
                                >
                                  <span className="summaryLabel">
                                    <Skeleton.Input
                                      active
                                      size="small"
                                      style={{ width: 70 }}
                                    />
                                  </span>
                                  <span className="summaryCount">
                                    <Skeleton.Input
                                      active
                                      size="small"
                                      style={{ width: 40 }}
                                    />
                                  </span>
                                </div>
                              ),
                            )
                          : header?.medal_summary?.map((summary) => {
                              const isTemplateType =
                                header?.ranking_type === "template";
                              const isFilterable = true;
                              const isSelected = isTemplateType
                                ? selectedRanks.includes(summary.rank_name)
                                : selectedScores.includes(summary.count);
                              const isDisabled =
                                isTemplateType && summary.count === 0;
                              const cardClassName = [
                                "snapshotSummaryCard",
                                isFilterable && isSelected ? "selected" : "",
                                isFilterable && isDisabled ? "disabled" : "",
                              ]
                                .filter(Boolean)
                                .join(" ");
                              const isClickable = isFilterable && !isDisabled;
                              const activateFilter = () => {
                                if (!isClickable) {
                                  return;
                                }
                                if (isTemplateType) {
                                  toggleRankFilter(
                                    projectSlug,
                                    rankingProjectId,
                                    summary.rank_name,
                                  );
                                } else {
                                  toggleScoreFilter(
                                    projectSlug,
                                    rankingProjectId,
                                    summary.count,
                                  );
                                }
                              };
                              return (
                                <div
                                  className={cardClassName}
                                  key={summary.rank_name}
                                  role={isClickable ? "button" : undefined}
                                  tabIndex={isClickable ? 0 : undefined}
                                  aria-pressed={
                                    isClickable ? isSelected : undefined
                                  }
                                  aria-label={
                                    isFilterable
                                      ? `Filter by ${summary.rank_name}`
                                      : undefined
                                  }
                                  onClick={activateFilter}
                                  onKeyDown={(e) => {
                                    if (!isClickable) {
                                      return;
                                    }
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      activateFilter();
                                    }
                                  }}
                                >
                                  <span className="summaryLabel">
                                    {summary.rank_name}
                                  </span>
                                  <span className="summaryCount">
                                    {summary.count}
                                  </span>
                                </div>
                              );
                            })}
                      </div>
                    )}
                    <div className="tableSettingBlk">
                      <Search
                        aria-label="Search Ranking Snapshot"
                        placeholder="Search jurisdiction, series"
                        allowClear
                        defaultValue=""
                        onChange={(e) => {
                          if (isLoading) {
                            return;
                          }
                          if (e.target.value === "" && searchTerm !== "") {
                            setSearch(projectSlug, rankingProjectId, "");
                          }
                        }}
                        onSearch={(value) => {
                          if (isLoading) {
                            return;
                          }
                          const trimmedValue = value.trim();
                          if (trimmedValue === searchTerm) {
                            return;
                          }
                          setSearch(
                            projectSlug,
                            rankingProjectId,
                            trimmedValue,
                          );
                        }}
                      />
                    </div>
                    <ErrorBoundary>
                      <RankingSnapshotTableView
                        projectSlug={projectSlug}
                        rankingProjectId={rankingProjectId}
                        rows={rows}
                        loading={isLoading}
                        skeletonRowCount={pageSize}
                        rankingType={header?.ranking_type}
                        onOpenRecord={(record) =>
                          navigate(
                            organizationBaseRouteUrl +
                              `/project/${projectDetails?.slug}/record/${record.record_id}`,
                          )
                        }
                      />
                    </ErrorBoundary>
                    <Paginator
                      disabled={isLoading}
                      currentPageSize={pageSize}
                      count={count}
                      setCurrentPageSize={(value: number) =>
                        setPageSize(projectSlug, rankingProjectId, value)
                      }
                      setCurrentPage={(page: number) =>
                        setPage(projectSlug, rankingProjectId, page)
                      }
                      getShowingDataString={getShowingDataString}
                      paginationRender={paginationRender}
                      currentPage={pageIndex}
                    />
                  </div>
                  {/* Reserved layout space for a future Audit History panel — not built by this
                      feature (out of spec.md's written scope), per Story Owner direction to leave
                      room for it rather than have this page later require a layout change. */}
                  <div className="snapshotAsideCol">
                    <h3>Audit History</h3>
                  </div>
                </div>
              </Fragment>
            )}
          </Fragment>
        )}
      </Fragment>
    </ErrorBoundary>
  );
};

export default OrgRankingSnapshotPage;
